import * as React from 'react';
import { Input, Field, makeStyles, tokens, Text, InputProps, useId, Toaster } from '@fluentui/react-components';
import { useNavigation } from '../hooks/routing';
import { useClient } from '../hooks/client';
import ProjectSelelctor from '../components/ProjectSelector';
import { LoadingButton } from '../components/LoadingButton';
import { StakeholderCreationRequest } from '../hooks/client/types/CreateEmailObjectRequest';
import { useItem } from '../hooks/item/ItemProvider';
import { DynamicConfiguration } from '../hooks/client/types/DynamicConfiguration';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/Button';
import useErrorHandling from '../hooks/useErrorHandling';

const useStyles = makeStyles({
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  header: {
    backgroundColor: tokens.colorNeutralStrokeOnBrand,
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBottom: tokens.spacingVerticalM,
  },
  headerText: {
    fontSize: tokens.fontSizeBase500,
  },
  container: {
    paddingInline: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  fields: {
    paddingBlock: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    flexGrow: 1,
  },
  buttonBar: {
    justifySelf: 'end',
    display: 'flex',
    justifyContent: 'end',
    gap: tokens.spacingHorizontalS,
    paddingInline: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalL,
  },
  button: {
    minWidth: 0,
  },
});

export const StakeholderScreen = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  const { params, navigate, goBack } = useNavigation<'stakeholders'>();
  const { configuration, createStakeholder, refetchConfiguration } = useClient();
  const { refetchStakeholders } = useItem();

  const shConfig = configuration.entityDefinitions.stakeholder;
  const fields = shConfig.fieldSchemaDetails;

  const [stakeholderInformation, setStakeholderInformation] = React.useState<StakeholderCreationRequest>({
    email: params.email,
    firstName: params.firstName ?? '',
    lastName: params.lastName ?? '',
    mobile: '',
    organisation: '',
    role: '',
    projectIds: params.projectIds ?? [],
  });

  const [isLoading, setIsLoading] = React.useState<boolean>();

  const toasterId = useId('stakeholder-toast');
  const { formErrors, handleError, clearErrors } = useErrorHandling(toasterId);

  const clearInvalidProjects = async () => {
    const freshConfiguration = await refetchConfiguration();
    setStakeholderInformation((curr) => {
      const filteredProjects = curr.projectIds.filter((x) => freshConfiguration.availableProjects.map((y) => y.id).includes(x));
      return { ...curr, projectIds: filteredProjects };
    });
  };

  const handleFormFieldChanged = React.useCallback(
    (key: keyof StakeholderCreationRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setStakeholderInformation((prev) => ({ ...prev, [key]: e.target.value }));
      clearErrors(key);
    },
    [clearErrors],
  );

  const handleCreateStakeholder = async () => {
    setIsLoading(true);
    try {
      const createdStakeholder = await createStakeholder(stakeholderInformation);
      await refetchStakeholders();
      navigate('main', { selectedTab: 'stakeholders', createdStakeholderInformation: createdStakeholder });
    } catch (error) {
      handleError(error, clearInvalidProjects);
    } finally {
      setIsLoading(false);
    }
  };

  const isValid =
    stakeholderInformation.projectIds?.length > 0 &&
    !!stakeholderInformation.firstName &&
    !!stakeholderInformation.lastName &&
    !!stakeholderInformation.email;

  const getValidationState = (field: keyof StakeholderCreationRequest) => {
    return formErrors[field]?.length ? 'error' : 'none';
  };

  return (
    <>
      <Toaster
        toasterId={toasterId}
        offset={{ vertical: 40 }}
      />
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <Text
            className={styles.headerText}
            as="h1"
          >
            {t('STAKEHOLDERS_SCREEN.ADD', { stakeholderPluralName: configuration.entityDefinitions.stakeholder.pluralName })}
          </Text>
        </div>
        <div className={styles.container}>
          <div className={styles.fields}>
            <Field
              label={configuration.entityDefinitions.project.pluralName}
              required
              validationState={getValidationState('projectIds')}
            >
              <ProjectSelelctor
                disabled={isLoading}
                projects={configuration.availableProjects}
                projectPluralName={configuration.entityDefinitions.project.pluralName}
                selectedProjects={stakeholderInformation.projectIds}
                onChange={(ids) => {
                  setStakeholderInformation((prev) => ({ ...prev, projectIds: ids }));
                  clearErrors('projectIds');
                }}
              />
            </Field>
            <Field
              label={fields.firstName.name}
              required
              validationState={getValidationState('firstName')}
            >
              <Input
                disabled={isLoading}
                value={stakeholderInformation.firstName}
                onChange={handleFormFieldChanged('firstName')}
              />
            </Field>
            <Field
              label={fields.lastName.name}
              required
              validationState={getValidationState('lastName')}
            >
              <Input
                disabled={isLoading}
                value={stakeholderInformation.lastName}
                onChange={handleFormFieldChanged('lastName')}
              />
            </Field>
            <Field
              label={fields.email.name}
              required
              validationState={getValidationState('email')}
            >
              <Input
                disabled
                value={stakeholderInformation.email}
                onChange={handleFormFieldChanged('email')}
              />
            </Field>
            <OptionalField
              handleFormFieldChanged={handleFormFieldChanged}
              disabled={isLoading}
              configuration={configuration}
              field="mobile"
              stakeholderInformation={stakeholderInformation}
              validationState={getValidationState('mobile')}
            />
            <OptionalField
              handleFormFieldChanged={handleFormFieldChanged}
              disabled={isLoading}
              configuration={configuration}
              field="role"
              stakeholderInformation={stakeholderInformation}
              validationState={getValidationState('role')}
            />
            <OptionalField
              handleFormFieldChanged={handleFormFieldChanged}
              disabled={isLoading}
              configuration={configuration}
              field="organisation"
              stakeholderInformation={stakeholderInformation}
              validationState={getValidationState('organisation')}
            />
          </div>
          <div className={styles.buttonBar}>
            <Button
              className={styles.button}
              disabled={isLoading}
              appearance="outline"
              onClick={() => goBack()}
            >
              {t('COMMON.CANCEL')}
            </Button>
            <LoadingButton
              className={styles.button}
              onClick={handleCreateStakeholder}
              loadingState={isLoading ? 'loading' : 'initial'}
              disabled={!isValid}
              appearance="primary"
            >
              {t('STAKEHOLDERS_SCREEN.CREATE')}
            </LoadingButton>
          </div>
        </div>
      </div>
    </>
  );
};

const OptionalField = ({
  configuration,
  field,
  disabled = false,
  stakeholderInformation,
  handleFormFieldChanged,
  validationState,
}: {
  configuration: DynamicConfiguration;
  field: keyof DynamicConfiguration['entityDefinitions']['stakeholder']['fieldSchemaDetails'];
  disabled: boolean;
  stakeholderInformation: StakeholderCreationRequest;
  handleFormFieldChanged: (key: string) => InputProps['onChange'];
  validationState?: 'error' | 'none';
}) => {
  const fields = configuration.entityDefinitions.stakeholder.fieldSchemaDetails;

  if (!fields[field]) return null;

  return (
    <Field
      label={fields[field].name}
      validationState={validationState}
    >
      <Input
        disabled={disabled}
        value={stakeholderInformation[field]}
        onChange={handleFormFieldChanged(field)}
      />
    </Field>
  );
};
